import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Building2, MapPin, Clock, CreditCard, CheckCircle2, XCircle,
  Loader2, ArrowRight, ArrowLeft, Phone, User, Store,
  FileText, Send, AlertCircle
} from 'lucide-react';
import AddressForm from '@/components/AddressForm';

// ==================== VALIDAÇÃO DE CNPJ ====================
const validateCNPJ = (cnpj) => {
  cnpj = cnpj.replace(/\D/g, '');
  
  if (cnpj.length !== 14) return false;
  
  const invalidCNPJs = [
    '00000000000000', '11111111111111', '22222222222222', '33333333333333',
    '44444444444444', '55555555555444', '66666666666666', '77777777777777',
    '88888888888888', '99999999999999'
  ];
  if (invalidCNPJs.includes(cnpj)) return false;
  
  const calcDigit = (digits, weights) => {
    let sum = 0;
    for (let i = 0; i < digits.length; i++) {
      sum += parseInt(digits[i]) * weights[i];
    }
    const digit = 11 - (sum % 11);
    return digit > 9 ? 0 : digit;
  };
  
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  
  return calcDigit(cnpj.substring(0, 12), weights1) === parseInt(cnpj[12]) &&
         calcDigit(cnpj.substring(0, 13), weights2) === parseInt(cnpj[13]);
};

const DAYS_OF_WEEK = [
  { id: 1, name: 'Segunda-feira', short: 'Seg' },
  { id: 2, name: 'Terça-feira', short: 'Ter' },
  { id: 3, name: 'Quarta-feira', short: 'Qua' },
  { id: 4, name: 'Quinta-feira', short: 'Qui' },
  { id: 5, name: 'Sexta-feira', short: 'Sex' },
  { id: 6, name: 'Sábado', short: 'Sáb' },
  { id: 0, name: 'Domingo', short: 'Dom' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return `${hour}:00`;
});

export default function ProviderOnboarding() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(2); // Começa na etapa 2 (já passou pelo registro)
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [cnpjValid, setCnpjValid] = useState(null);
  const [reviewData, setReviewData] = useState(null);

  // Estado do formulário
  const [companyData, setCompanyData] = useState({
    cnpj: '',
    razao_social: '',
    nome_fantasia: '',
    responsavel_legal: '',
    telefone_comercial: ''
  });

  const [addressData, setAddressData] = useState({
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  });

  const [operatingData, setOperatingData] = useState({
    days: DAYS_OF_WEEK.map(d => ({
      day: d.id,
      enabled: d.id >= 1 && d.id <= 5, // Segunda a Sexta por padrão
      start: '08:00',
      end: '18:00'
    })),
    daily_limit: 30
  });

  const [pixData, setPixData] = useState({
    pix_type: '',
    pix_key: '',
    holder_name: ''
  });

  // Verificar autenticação e buscar status
  useEffect(() => {
    if (!user || user.user_type !== 'provider') {
      navigate('/provider/login');
      return;
    }
    fetchOnboardingStatus();
  }, [user, navigate]);

  const fetchOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/provider/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOnboardingStatus(res.data);
      
      // Se onboarding completo, redirecionar para dashboard
      if (res.data.provider_status === 'ACTIVE') {
        navigate('/provider/dashboard');
        return;
      }
      
      // Status legado PENDING_REVIEW (prestadores antigos): tratar como ACTIVE
      // para evitar tela "Cadastro em Análise" — JATOO não usa aprovação manual.
      if (res.data.provider_status === 'PENDING_REVIEW') {
        navigate('/provider/dashboard');
        return;
      }
      
      // Definir etapa atual
      if (res.data.next_step) {
        setCurrentStep(res.data.next_step);
      }
    } catch (error) {
      console.error('Erro ao buscar status:', error);
    }
  };

  // Validar CNPJ em tempo real
  const handleCnpjChange = (value) => {
    setCompanyData({ ...companyData, cnpj: value });
    const clean = value.replace(/\D/g, '');
    if (clean.length === 14) {
      setCnpjValid(validateCNPJ(clean));
    } else {
      setCnpjValid(null);
    }
  };

  // Buscar CEP
  // (movido para o componente reutilizável AddressForm)

  // Toggle dia de funcionamento
  const toggleDay = (dayId) => {
    setOperatingData(prev => ({
      ...prev,
      days: prev.days.map(d => 
        d.day === dayId ? { ...d, enabled: !d.enabled } : d
      )
    }));
  };

  // Atualizar horário
  const updateDayHours = (dayId, field, value) => {
    setOperatingData(prev => ({
      ...prev,
      days: prev.days.map(d => 
        d.day === dayId ? { ...d, [field]: value } : d
      )
    }));
  };

  // ==================== SUBMIT HANDLERS ====================
  const handleStep2Submit = async (e) => {
    e.preventDefault();
    
    const cnpj = companyData.cnpj.replace(/\D/g, '');
    if (cnpj.length !== 14 || !validateCNPJ(cnpj)) {
      toast.error('CNPJ inválido');
      return;
    }
    
    if (!companyData.razao_social || companyData.razao_social.length < 3) {
      toast.error('Razão Social deve ter pelo menos 3 caracteres');
      return;
    }
    
    if (!companyData.nome_fantasia || companyData.nome_fantasia.length < 3) {
      toast.error('Nome Fantasia deve ter pelo menos 3 caracteres');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/onboarding/step2`, {
        cnpj: cnpj,
        razao_social: companyData.razao_social,
        nome_fantasia: companyData.nome_fantasia,
        responsavel_legal: companyData.responsavel_legal,
        telefone_comercial: companyData.telefone_comercial
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Dados da empresa salvos!');
      setCurrentStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3Submit = async (e) => {
    e.preventDefault();
    
    if (!addressData.cep || !addressData.street || !addressData.number || !addressData.city) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/provider/onboarding/step3`, {
        address: addressData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // PROMPT BLINDADO: Mostrar aviso leve se geocoding falhou, mas não bloquear
      if (response.data.geocoding_warning) {
        toast.warning('Endereço salvo! Não foi possível obter a localização exata, mas o cadastro continuará normalmente.', {
          duration: 5000
        });
      } else {
        toast.success('Endereço salvo!');
      }
      setCurrentStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar endereço');
    } finally {
      setLoading(false);
    }
  };

  const handleStep4Submit = async (e) => {
    e.preventDefault();
    
    const enabledDays = operatingData.days.filter(d => d.enabled);
    if (enabledDays.length === 0) {
      toast.error('Selecione pelo menos um dia de funcionamento');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/onboarding/step4`, {
        operating_days: operatingData.days,
        daily_limit: operatingData.daily_limit
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Horários salvos!');
      setCurrentStep(5);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar horários');
    } finally {
      setLoading(false);
    }
  };

  const handleStep5Submit = async (e) => {
    e.preventDefault();
    
    if (!pixData.pix_type || !pixData.pix_key || !pixData.holder_name) {
      toast.error('Preencha todos os campos do PIX');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/onboarding/step5`, pixData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Dados de pagamento salvos!');
      
      // Buscar dados para revisão
      const reviewRes = await axios.get(`${API}/provider/onboarding/review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviewData(reviewRes.data);
      setCurrentStep(6);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar dados de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleStep6Submit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/provider/onboarding/step6`, {
        confirm: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Cadastro concluído! Bem-vindo à JATOO.');
      navigate('/provider/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao concluir cadastro');
    } finally {
      setLoading(false);
    }
  };

  // ==================== RENDER STEPS ====================
  const renderProgress = () => {
    const steps = [
      { num: 1, label: 'Conta', icon: User },
      { num: 2, label: 'Empresa', icon: Building2 },
      { num: 3, label: 'Endereço', icon: MapPin },
      { num: 4, label: 'Horários', icon: Clock },
      { num: 5, label: 'PIX', icon: CreditCard },
      { num: 6, label: 'Revisão', icon: FileText },
    ];
    
    return (
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-primary">
            Etapa {Math.min(currentStep, 6)} de 6
          </span>
          <span className="text-xs text-muted-foreground">
            {steps[Math.min(currentStep - 1, 5)]?.label}
          </span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary rounded-full transition-all duration-500" 
            style={{ width: `${(Math.min(currentStep, 6) / 6) * 100}%` }} 
          />
        </div>
        <div className="flex justify-between mt-2">
          {steps.map((step) => {
            const Icon = step.icon;
            const isCompleted = currentStep > step.num;
            const isCurrent = currentStep === step.num;
            
            return (
              <div 
                key={step.num}
                className={`flex flex-col items-center ${
                  isCompleted ? 'text-primary' : isCurrent ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${
                  isCompleted ? 'bg-primary text-white' : isCurrent ? 'bg-primary/20 text-primary' : 'bg-slate-100'
                }`}>
                  {isCompleted ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <span className="text-[10px] mt-1 hidden sm:block">{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ETAPA 2: Dados da Empresa
  const renderStep2 = () => (
    <form onSubmit={handleStep2Submit} className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-primary/10 p-3 rounded-xl">
          <Building2 className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Dados da Empresa</h2>
          <p className="text-sm text-muted-foreground">Informações do CNPJ</p>
        </div>
      </div>

      <Alert className="bg-amber-50 border-amber-200 mb-4">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-sm text-amber-800">
          <strong>Obrigatório:</strong> Somente empresas (CNPJ) podem ser prestadores.
        </AlertDescription>
      </Alert>

      <div>
        <Label htmlFor="cnpj">CNPJ *</Label>
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="cnpj"
            placeholder="00.000.000/0000-00"
            className={`pl-10 pr-10 ${cnpjValid === true ? 'border-green-500' : cnpjValid === false ? 'border-red-500' : ''}`}
            value={companyData.cnpj}
            onChange={(e) => handleCnpjChange(e.target.value)}
            required
            data-testid="cnpj-input"
          />
          {cnpjValid === true && <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />}
          {cnpjValid === false && <XCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />}
        </div>
        {cnpjValid === false && <p className="text-xs text-red-500 mt-1">CNPJ inválido</p>}
      </div>

      <div>
        <Label htmlFor="razao_social">Razão Social *</Label>
        <Input
          id="razao_social"
          placeholder="Nome registrado na Receita Federal"
          value={companyData.razao_social}
          onChange={(e) => setCompanyData({ ...companyData, razao_social: e.target.value })}
          required
          data-testid="razao-social-input"
        />
      </div>

      <div>
        <Label htmlFor="nome_fantasia">Nome Fantasia (Lava-Jato) *</Label>
        <div className="relative">
          <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="nome_fantasia"
            placeholder="Ex: Auto Lavagem Express"
            className="pl-10"
            value={companyData.nome_fantasia}
            onChange={(e) => setCompanyData({ ...companyData, nome_fantasia: e.target.value })}
            required
            data-testid="nome-fantasia-input"
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1">Este nome será exibido para os clientes</p>
      </div>

      <div>
        <Label htmlFor="responsavel">Nome do Responsável Legal *</Label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="responsavel"
            placeholder="Nome completo"
            className="pl-10"
            value={companyData.responsavel_legal}
            onChange={(e) => setCompanyData({ ...companyData, responsavel_legal: e.target.value })}
            required
            data-testid="responsavel-input"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="telefone">Telefone Comercial *</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            id="telefone"
            placeholder="(11) 99999-9999"
            className="pl-10"
            value={companyData.telefone_comercial}
            onChange={(e) => setCompanyData({ ...companyData, telefone_comercial: e.target.value })}
            required
            data-testid="telefone-input"
          />
        </div>
      </div>

      <Button type="submit" className="w-full h-12" disabled={loading || cnpjValid !== true} data-testid="step2-submit">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Continuar <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </form>
  );

  // ETAPA 3: Endereço
  const renderStep3 = () => (
    <form onSubmit={handleStep3Submit} className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-primary/10 p-3 rounded-xl">
          <MapPin className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Endereço do Lava-Jato</h2>
          <p className="text-sm text-muted-foreground">Onde os clientes irão deixar os veículos</p>
        </div>
      </div>

      <AddressForm value={addressData} onChange={setAddressData} />

      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={() => setCurrentStep(2)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
        <Button type="submit" className="flex-1" disabled={loading} data-testid="step3-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Continuar <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );

  // ETAPA 4: Horários
  const renderStep4 = () => (
    <form onSubmit={handleStep4Submit} className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-primary/10 p-3 rounded-xl">
          <Clock className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Horários de Funcionamento</h2>
          <p className="text-sm text-muted-foreground">Quando você atende clientes</p>
        </div>
      </div>

      <div className="space-y-3">
        {DAYS_OF_WEEK.map(day => {
          const dayData = operatingData.days.find(d => d.day === day.id);
          return (
            <div key={day.id} className={`p-3 rounded-lg border ${dayData?.enabled ? 'border-primary bg-primary/5' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={dayData?.enabled}
                    onCheckedChange={() => toggleDay(day.id)}
                    data-testid={`day-${day.id}-check`}
                  />
                  <span className={`font-medium ${dayData?.enabled ? 'text-primary' : 'text-muted-foreground'}`}>
                    {day.name}
                  </span>
                </div>
                
                {dayData?.enabled && (
                  <div className="flex items-center gap-2">
                    <Select 
                      value={dayData.start} 
                      onValueChange={(v) => updateDayHours(day.id, 'start', v)}
                    >
                      <SelectTrigger className="w-24 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.filter(h => parseInt(h) >= 6 && parseInt(h) <= 22).map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-muted-foreground">às</span>
                    <Select 
                      value={dayData.end} 
                      onValueChange={(v) => updateDayHours(day.id, 'end', v)}
                    >
                      <SelectTrigger className="w-24 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOURS.filter(h => parseInt(h) >= 6 && parseInt(h) <= 23).map(h => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <Label htmlFor="daily_limit">Limite Diário de Atendimentos</Label>
        <Select 
          value={operatingData.daily_limit.toString()} 
          onValueChange={(v) => setOperatingData({ ...operatingData, daily_limit: parseInt(v) })}
        >
          <SelectTrigger data-testid="daily-limit-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[10, 15, 20, 25, 30, 35, 40, 45, 50].map(n => (
              <SelectItem key={n} value={n.toString()}>{n} atendimentos</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={() => setCurrentStep(3)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
        <Button type="submit" className="flex-1" disabled={loading} data-testid="step4-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Continuar <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );

  // ETAPA 5: PIX
  const renderStep5 = () => (
    <form onSubmit={handleStep5Submit} className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-primary/10 p-3 rounded-xl">
          <CreditCard className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Dados de Pagamento</h2>
          <p className="text-sm text-muted-foreground">Chave PIX para receber pagamentos</p>
        </div>
      </div>

      <Alert className="bg-blue-50 border-blue-200">
        <CreditCard className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-sm text-blue-800">
          Os pagamentos serão enviados para esta chave PIX após cada serviço concluído.
        </AlertDescription>
      </Alert>

      <div>
        <Label htmlFor="pix_type">Tipo de Chave PIX *</Label>
        <Select value={pixData.pix_type} onValueChange={(v) => setPixData({ ...pixData, pix_type: v })}>
          <SelectTrigger data-testid="pix-type-select">
            <SelectValue placeholder="Selecione o tipo..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="cnpj">CNPJ</SelectItem>
            <SelectItem value="email">E-mail</SelectItem>
            <SelectItem value="random">Chave Aleatória</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="pix_key">Chave PIX *</Label>
        <Input
          id="pix_key"
          placeholder={
            pixData.pix_type === 'cnpj' ? '00.000.000/0000-00' :
            pixData.pix_type === 'email' ? 'email@exemplo.com' :
            'Chave aleatória'
          }
          value={pixData.pix_key}
          onChange={(e) => setPixData({ ...pixData, pix_key: e.target.value })}
          required
          data-testid="pix-key-input"
        />
      </div>

      <div>
        <Label htmlFor="holder_name">Nome do Titular *</Label>
        <Input
          id="holder_name"
          placeholder="Nome conforme cadastrado no PIX"
          value={pixData.holder_name}
          onChange={(e) => setPixData({ ...pixData, holder_name: e.target.value })}
          required
          data-testid="holder-name-input"
        />
      </div>

      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={() => setCurrentStep(4)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
        <Button type="submit" className="flex-1" disabled={loading} data-testid="step5-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Continuar <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </form>
  );

  // ETAPA 6: Revisão
  const renderStep6 = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-primary/10 p-3 rounded-xl">
          <FileText className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Revisão Final</h2>
          <p className="text-sm text-muted-foreground">Confirme os dados antes de enviar</p>
        </div>
      </div>

      {reviewData && (
        <div className="space-y-4">
          {/* Empresa */}
          <Card className="p-4">
            <h3 className="font-semibold flex items-center gap-2 mb-3">
              <Building2 className="w-4 h-4" /> Dados da Empresa
            </h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground">CNPJ:</span> {reviewData.company.cnpj_masked}</div>
              <div><span className="text-muted-foreground">Razão Social:</span> {reviewData.company.razao_social}</div>
              <div><span className="text-muted-foreground">Nome Fantasia:</span> {reviewData.company.nome_fantasia}</div>
              <div><span className="text-muted-foreground">Responsável:</span> {reviewData.company.responsavel_legal}</div>
            </div>
          </Card>

          {/* Endereço */}
          <Card className="p-4">
            <h3 className="font-semibold flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4" /> Endereço
            </h3>
            <p className="text-sm">{reviewData.address.full_address}</p>
          </Card>

          {/* Funcionamento */}
          <Card className="p-4">
            <h3 className="font-semibold flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4" /> Funcionamento
            </h3>
            <div className="text-sm">
              <p><span className="text-muted-foreground">Dias:</span> {reviewData.operating.working_days_display.join(', ')}</p>
              <p><span className="text-muted-foreground">Limite diário:</span> {reviewData.operating.daily_limit} atendimentos</p>
            </div>
          </Card>

          {/* PIX */}
          <Card className="p-4">
            <h3 className="font-semibold flex items-center gap-2 mb-3">
              <CreditCard className="w-4 h-4" /> Dados de Pagamento
            </h3>
            <div className="text-sm">
              <p><span className="text-muted-foreground">Tipo:</span> {reviewData.pix.type?.toUpperCase()}</p>
              <p><span className="text-muted-foreground">Chave:</span> {reviewData.pix.key_masked}</p>
              <p><span className="text-muted-foreground">Titular:</span> {reviewData.pix.holder_name}</p>
            </div>
          </Card>
        </div>
      )}

      <Alert className="bg-amber-50 border-amber-200">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-sm text-amber-800">
          Ao concluir, seu cadastro será ativado e você terá acesso ao painel.
        </AlertDescription>
      </Alert>

      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" className="flex-1" onClick={() => setCurrentStep(5)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
        </Button>
        <Button onClick={handleStep6Submit} className="flex-1" disabled={loading} data-testid="step6-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Concluir Cadastro
        </Button>
      </div>
    </div>
  );

  // ETAPA 7: Aguardando aprovação
  const renderPendingReview = () => (
    <div className="text-center py-8">
      <div className="bg-amber-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
        <Clock className="w-10 h-10 text-amber-600" />
      </div>
      <h2 className="text-2xl font-bold mb-2">Cadastro em Análise</h2>
      <p className="text-muted-foreground mb-6">
        Seu cadastro foi enviado e está sendo analisado pela nossa equipe.
        Você será notificado por e-mail assim que for aprovado.
      </p>
      <Alert className="bg-blue-50 border-blue-200 text-left">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-sm text-blue-800">
          Tempo médio de análise: 24 a 48 horas úteis
        </AlertDescription>
      </Alert>
      <Button variant="outline" className="mt-6" onClick={logout}>
        Sair
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-xl p-8">
        {currentStep <= 6 && renderProgress()}
        
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderStep5()}
        {currentStep === 6 && renderStep6()}
        {currentStep === 7 && renderPendingReview()}
      </Card>
    </div>
  );
}
